import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO, KitEnrollmentContext } from "../../../../models/ArticleDTO";
import { owedArticleStatus } from "../../../../constants";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import KitHistoryModal from "../../../../components/kits/kitHistoryModal";
import KitPaymentPrompt from "../../../../components/kits/kitPaymentPrompt";
import {
	AdvisorsCell, Empty, EnrollmentCell, FilterBar, KitFilters, Loading, ManagerCell, PER_PAGE_DEFAULT, Pager, PaidDatesCell, PaymentCell, ProductCell, SortTh, StatusCell, StudentCell,
	apiError, bestEnrollment, cancelReasonLabel, daysAgo, money, monthStart, saveExcel, shortName, today, ui, useCampus, useDebounced, useFilterOptions, useIdleLocked, usePerms
} from "./shared";

/* Tienda → Control de Ventas (section "kitSales"): Reporte de Ventas · Inscritos Recientes · Abonos sin Liquidar · Alumnos sin Producto · Anulaciones */

type TabKey = "ventas" | "inscritos" | "abonos" | "sin_producto" | "anulaciones";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "ventas", label: "Reporte de Ventas" },
	{ key: "inscritos", label: "Inscritos Recientes" },
	{ key: "abonos", label: "Abonos sin Liquidar" },
	{ key: "sin_producto", label: "Alumnos sin Producto" },
	{ key: "anulaciones", label: "Anulaciones" }
];
const SECTION = "kitSales";

const defaultFilters = (tab: TabKey): KitFilters => {
	switch (tab) {
		case "ventas": return { paidFrom: monthStart(), paidTo: today(), enrolledFrom: daysAgo(30), enrolledTo: today(), sort: "lastPaid", dir: "desc" };
		case "inscritos": return { enrolledFrom: daysAgo(30), enrolledTo: today() };
		case "abonos": return { sort: "lastPaid", dir: "desc" };
		case "sin_producto": return { onlyEnrolled: "1" };
		default: return { from: daysAgo(30), to: today(), productsOnly: "0" };
	}
};

interface StudentRow { studentId: number; identityCode: string; name: string; phone: string | null; email: string | null; enrollments: KitEnrollmentContext[]; kitManager: any }
interface EnrolleeRow { student: { id: number; name: string; identityCode: string; phone: string | null }; enrollment: KitEnrollmentContext & { advisors?: string | null }; kit: ArticleDTO | null; kitManager: any }

const pctColor = (pct: number | null): string => pct === null ? "var(--wa-apagado)" : pct < 75 ? "var(--wa-oro)" : pct < 90 ? "var(--wa-oro)" : "var(--wa-verde)";

const ControlVentas: React.FC = () => {
	const { campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canCancel = !!perms.create; // CREATE bit of "kitSales": anular, reactivar y reportar pago
	const options = useFilterOptions(campusId, SECTION);
	const http = HttpManager.getInstance();

	const [tabKey, setTabKey] = useState<TabKey>("ventas");
	const [cancelMode, setCancelMode] = useState<"pending_unpaid" | "cancelled">("pending_unpaid");
	const [filters, setFilters] = useState<KitFilters>(defaultFilters("ventas"));
	const debouncedQ = useDebounced(filters.q || "");
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PER_PAGE_DEFAULT);
	const [rows, setRows] = useState<ArticleDTO[]>([]);
	const [students, setStudents] = useState<StudentRow[]>([]);
	const [enrollees, setEnrollees] = useState<EnrolleeRow[]>([]);
	const [totals, setTotals] = useState<any | null>(null);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [exporting, setExporting] = useState<boolean>(false);
	const [historyKit, setHistoryKit] = useState<ArticleDTO | null>(null);
	const [kitAction, setKitAction] = useState<{ type: "cancel" | "reactivate"; kit: ArticleDTO } | null>(null);
	const [kitActionBusy, setKitActionBusy] = useState<boolean>(false);
	const [kitActionError, setKitActionError] = useState<string | null>(null);
	const [paymentKit, setPaymentKit] = useState<ArticleDTO | null>(null);
	const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
	/** Only the latest request may paint the table (switching tabs fast must not show the previous tab's answer). */
	const requestRef = useRef<number>(0);

	const effective = useCallback((): KitFilters => ({ ...filters, q: debouncedQ.trim() }), [filters, debouncedQ]);

	const load = useCallback(async () => {
		if (!campusId) return;
		const request = ++requestRef.current;
		const current = () => request === requestRef.current;
		setLoading(true);
		setError(null);
		try {
			const f = effective();
			if (tabKey === "ventas") {
				const [list, sums] = await Promise.all([http.kitsService.list(campusId, SECTION, "sold", f, page, perPage), http.kitsService.salesTotals(campusId, SECTION, f)]);
				if (!current()) return;
				setRows(list.data.data || []); setTotal(list.data.total || 0); setLastPage(list.data.last_page || 1); setTotals(sums.data);
			} else if (tabKey === "inscritos") {
				const [list, sums] = await Promise.all([http.kitsService.recentEnrollees(campusId, SECTION, f, page, perPage), http.kitsService.salesTotals(campusId, SECTION, f)]);
				if (!current()) return;
				setEnrollees(list.data.data || []); setTotal(list.data.total || 0); setLastPage(list.data.last_page || 1); setTotals(sums.data);
			} else if (tabKey === "abonos") {
				const response = await http.kitsService.list(campusId, SECTION, "partially_paid", f, page, perPage);
				if (!current()) return;
				setRows(response.data.data || []); setTotal(response.data.total || 0); setLastPage(response.data.last_page || 1);
			} else if (tabKey === "sin_producto") {
				const response = await http.kitsService.studentsWithoutProducts(campusId, SECTION, f, page, perPage);
				if (!current()) return;
				setStudents(response.data.data || []); setTotal(response.data.total || 0); setLastPage(response.data.last_page || 1);
			} else {
				const response = await http.kitsService.list(campusId, SECTION, cancelMode, f, page, perPage);
				if (!current()) return;
				setRows(response.data.data || []); setTotal(response.data.total || 0); setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			if (!current()) return;
			setError(apiError(requestError, "No se pudo cargar la información."));
			setRows([]); setStudents([]); setEnrollees([]);
		} finally {
			if (current()) setLoading(false);
		}
	}, [campusId, tabKey, cancelMode, effective, page, perPage]);

	useEffect(() => { load(); }, [load]);

	const patch = (changes: Partial<KitFilters>) => { setFilters((previous) => ({ ...previous, ...changes })); setPage(1); };
	const onSort = (key: string) => patch({ sort: key, dir: filters.sort === key && filters.dir === "desc" ? "asc" : "desc" });
	const switchTab = (key: TabKey) => { setTabKey(key); setPage(1); setFilters(defaultFilters(key)); setRows([]); setTotals(null); };

	const onKitAction = async (reason: string) => {
		if (!kitAction) return;
		setKitActionBusy(true);
		setKitActionError(null);
		try {
			if (kitAction.type === "cancel") await http.kitsService.cancel(campusId, kitAction.kit.id, reason);
			else await http.kitsService.reactivate(campusId, kitAction.kit.id, reason);
			setKitAction(null);
			await load();
		} catch (requestError: any) {
			setKitActionError(apiError(requestError, "No se pudo completar la operación."));
		} finally {
			setKitActionBusy(false);
		}
	};

	const exportExcel = async () => {
		setExporting(true);
		try {
			const kind = tabKey === "inscritos" ? "enrollees" : tabKey === "sin_producto" ? "students" : "kits";
			const tab = tabKey === "ventas" ? "sold" : tabKey === "abonos" ? "partially_paid" : tabKey === "anulaciones" ? cancelMode : "all";
			await saveExcel(campusId, SECTION, kind, tab, effective(), `ventas_${tabKey}_${today()}`);
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo generar el Excel."));
		} finally {
			setExporting(false);
		}
	};

	// ---------------------------------------------------------------- pieces

	const historyButton = (kit: ArticleDTO) => <button type="button" className={ui.btn} disabled={idleLocked} onClick={() => setHistoryKit(kit)}>Historial</button>;

	const kpiCard = (label: string, value: string, color?: string, hint?: string) => <div key={label} style={ui.kpi}>
		<div style={ui.caption}>{label}</div>
		<div style={{ fontSize: "20px", fontWeight: 700, color: color || "var(--wa-tinta)" }}>{value}</div>
		{hint ? <div style={ui.small}>{hint}</div> : null}
	</div>;

	const enrolledKpi = () => totals ? kpiCard("% Inscritos con Kit", totals.enrolledWithKitPct === null ? "—" : totals.enrolledWithKitPct + "%", pctColor(totals.enrolledWithKitPct),
		`${totals.enrolledWithKit} de ${totals.enrolled} inscritos · ${totals.enrolledFrom} a ${totals.enrolledTo}`) : null;

	const salesKpis = () => totals ? <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", margin: "10px 0" }}>
		{kpiCard("Kits Completos Vendidos", String(totals.kitsComplete), undefined, totals.kitsPartial ? `+ ${totals.kitsPartial} con abono` : undefined)}
		{kpiCard("Productos de Vitrina Vendidos", String(totals.vitrina))}
		{enrolledKpi()}
		{kpiCard("Vendido", money(currency, totals.sold))}
		{kpiCard("Cobrado", money(currency, totals.paid))}
		{kpiCard("Saldo por Cobrar", money(currency, totals.pending), totals.pending > 0 ? "var(--wa-oro)" : "var(--wa-tinta)")}
	</div> : null;

	const kitTable = (showPayStatusHint: boolean) => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<SortTh label="Alumno" sortKey="student" sort={filters.sort} dir={filters.dir} onSort={onSort} />
			<SortTh label="Producto" sortKey="product" sort={filters.sort} dir={filters.dir} onSort={onSort} />
			<th style={ui.th}>Curso · Horario · Profesor</th>
			<SortTh label="Último Pago" sortKey="lastPaid" sort={filters.sort} dir={filters.dir} onSort={onSort} />
			<SortTh label="Pagado / Total" sortKey="amount" align="right" sort={filters.sort} dir={filters.dir} onSort={onSort} />
			<th style={ui.th}>Vendido por</th>
			<th style={{ ...ui.th, ...ui.center }}>Status</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{rows.map((kit) => <tr key={kit.id}>
			<td style={ui.td}><StudentCell kit={kit} /></td>
			<td style={ui.td}><ProductCell kit={kit} />{showPayStatusHint ? <div style={ui.small}>{kit.status}</div> : null}</td>
			<td style={ui.td}><EnrollmentCell enrollment={bestEnrollment(kit)} extra={(kit.enrollments || []).length - 1} /></td>
			<td style={ui.td}><PaidDatesCell kit={kit} /></td>
			<td style={ui.td}><PaymentCell kit={kit} /></td>
			<td style={ui.td}><AdvisorsCell kit={kit} /></td>
			<td style={{ ...ui.td, ...ui.center }}><StatusCell kit={kit} /></td>
			<td style={ui.td}>{historyButton(kit)}</td>
		</tr>)}</tbody>
	</table>;

	const enrolleesTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}>Alumno</th>
			<th style={ui.th}>Curso · Horario · Profesor</th>
			<th style={ui.th}>Inscrito</th>
			<th style={ui.th}>Asesora matrícula</th>
			<th style={ui.th}>Kit</th>
			<th style={ui.th}>Último Pago</th>
			<th style={{ ...ui.th, ...ui.right }}>Pagado / Total</th>
			<th style={ui.th}>Vendido por</th>
			<th style={ui.th}>Gestora kits</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{enrollees.map((row) => <tr key={row.enrollment.enrollmentId} style={{ background: row.kit ? undefined : "var(--wa-oro-suave)" }}>
			<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.student.name}</div><div style={ui.small}>{row.student.identityCode}{row.student.phone ? " · " + row.student.phone : ""}</div></td>
			<td style={ui.td}><EnrollmentCell enrollment={row.enrollment} /></td>
			<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.enrollment.enrolledAt}</td>
			<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.enrollment.advisors ? row.enrollment.advisors.split(" / ").map(shortName).join(", ") : <span style={ui.muted}>—</span>}</td>
			<td style={ui.td}>{row.kit ? <ProductCell kit={row.kit} /> : <span style={{ color: "var(--wa-oro)", fontWeight: 600 }}>Sin kit</span>}</td>
			<td style={ui.td}>{row.kit ? <PaidDatesCell kit={row.kit} /> : <span style={ui.muted}>—</span>}</td>
			<td style={ui.td}>{row.kit ? <PaymentCell kit={row.kit} /> : <span style={ui.muted}>—</span>}</td>
			<td style={ui.td}>{row.kit ? <AdvisorsCell kit={row.kit} /> : <span style={ui.muted}>—</span>}</td>
			<td style={ui.td}><ManagerCell manager={row.kitManager} /></td>
			<td style={ui.td}>{row.kit ? historyButton(row.kit) : null}</td>
		</tr>)}</tbody>
	</table>;

	const studentsTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}>Alumno</th>
			<th style={ui.th}>Curso</th>
			<th style={ui.th}>Periodo</th>
			<th style={ui.th}>Horario</th>
			<th style={ui.th}>Profesor</th>
			<th style={ui.th}>Inscrito</th>
			<th style={ui.th}>Gestora kits</th>
		</tr></thead>
		<tbody>{students.map((student) => {
			const enrollments: (KitEnrollmentContext | null)[] = student.enrollments.length ? student.enrollments : [null];
			return enrollments.map((enrollment, index) => <tr key={student.studentId + "_" + index}>
				{index === 0 ? <td style={ui.td} rowSpan={enrollments.length}>
					<div style={{ fontWeight: 600 }}>{student.name}</div>
					<div style={ui.small}>{student.identityCode}{student.phone ? " · " + student.phone : ""}</div>
				</td> : null}
				{enrollment ? <>
					<td style={{ ...ui.td, fontWeight: 600 }}>{enrollment.courseBrief || enrollment.module || enrollment.level}</td>
					<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{enrollment.period || "—"}</td>
					<td style={ui.td}>{enrollment.schedule || enrollment.scheduleName || enrollment.scheduleCode}</td>
					<td style={ui.td}>{enrollment.mentor || <span style={ui.muted}>—</span>}</td>
					<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{enrollment.enrolledAt}</td>
				</> : <td style={{ ...ui.td, ...ui.muted }} colSpan={5}>Sin horario activo</td>}
				{index === 0 ? <td style={ui.td} rowSpan={enrollments.length}><ManagerCell manager={student.kitManager} /></td> : null}
			</tr>);
		})}</tbody>
	</table>;

	const cancellationsTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}>Alumno</th>
			<th style={ui.th}>Producto</th>
			<th style={ui.th}>Curso · Horario · Profesor</th>
			<SortTh label="Cargado" sortKey="charged" sort={filters.sort} dir={filters.dir} onSort={onSort} />
			<th style={{ ...ui.th, ...ui.right }}>Total</th>
			<th style={ui.th}>Estado</th>
			<th style={ui.th}>Vendido por</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{rows.map((kit) => <tr key={kit.id} style={{ opacity: kit.isCancelled ? 0.85 : 1 }}>
			<td style={ui.td}><StudentCell kit={kit} /></td>
			<td style={ui.td}><ProductCell kit={kit} /></td>
			<td style={ui.td}><EnrollmentCell enrollment={bestEnrollment(kit)} extra={(kit.enrollments || []).length - 1} /></td>
			<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{kit.createdAt}{kit.createdBy ? <div style={ui.small}>por {shortName(kit.createdBy)}</div> : null}</td>
			<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>{kit.subTotal.moneyFormat}</td>
			<td style={ui.td}>{kit.isCancelled
				? <><div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>Anulado</div><div style={ui.small}>{cancelReasonLabel(kit.cancelReason)}{kit.cancelledAt ? " · " + kit.cancelledAt : ""}</div></>
				: kit.paymentIntentId && kit.paymentIntentStatus === "Por procesar"
					? <><div style={{ fontWeight: 600, color: "var(--wa-acento-texto)" }}>Pago reportado</div><div style={ui.small}>Ticket #{kit.paymentIntentId} en Pagos por Procesar</div></>
					: <><div style={{ fontWeight: 600 }}>Sin pago</div><div style={ui.small}>Se anula solo al cierre del día</div></>}</td>
			<td style={ui.td}><AdvisorsCell kit={kit} /></td>
			<td style={ui.td}><div style={{ display: "flex", gap: "6px", whiteSpace: "nowrap" }}>
				{historyButton(kit)}
				{canCancel && !kit.isCancelled && kit.statusId === owedArticleStatus.PENDING && kit.paidAmount.value === 0 && !(kit.paymentIntentId && kit.paymentIntentStatus === "Por procesar")
					? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill waOroTexto" disabled={idleLocked} onClick={() => { setKitActionError(null); setKitAction({ type: "cancel", kit }); }}>Anular</button> : null}
				{canCancel && kit.isCancelled
					? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={idleLocked} onClick={() => { setKitActionError(null); setKitAction({ type: "reactivate", kit }); }}>Reactivar</button> : null}
				{canCancel && !kit.isCancelled && kit.statusId < owedArticleStatus.FULLY_PAID && !(kit.paymentIntentId && kit.paymentIntentStatus === "Por procesar")
					? <button type="button" className={ui.btnPrimary} disabled={idleLocked} onClick={() => setPaymentKit(kit)}>Reportar Pago</button> : null}
			</div></td>
		</tr>)}</tbody>
	</table>;

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	const unit = tabKey === "sin_producto" ? "alumnos" : tabKey === "inscritos" ? "inscritos" : "kits";
	const show = tabKey === "ventas" ? { search: true, course: true, period: true, schedule: true, mentor: true, weekday: true, article: true, advisor: true, paymentStatus: true, firstPaidRange: true, enrolledRange: true }
		: tabKey === "inscritos" ? { search: true, course: true, period: true, schedule: true, mentor: true, weekday: true, kitStatus: true, enrolledRange: true }
		: tabKey === "abonos" ? { search: true, course: true, period: true, schedule: true, mentor: true, weekday: true, article: true, advisor: true, firstPaidRange: true }
		: tabKey === "sin_producto" ? { search: true, course: true, period: true, schedule: true, mentor: true, weekday: true, onlyEnrolled: true }
		: { search: true, course: true, period: true, schedule: true, article: true, chargedRange: true };

	return <div style={ui.page}>
		<h2 style={ui.title}>Control de Ventas</h2>
		<div style={ui.tabs}>
			{TABS.map((t) => <button key={t.key} type="button" disabled={idleLocked} className={"btn rounded-pill " + (t.key === tabKey ? "btn-primary" : "btn-outline-secondary")} onClick={() => switchTab(t.key)}>{t.label}</button>)}
			{tabKey === "anulaciones" ? <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
				<button type="button" className={"btn btn-sm rounded-pill " + (cancelMode === "pending_unpaid" ? "btn-primary" : "btn-outline-secondary")} disabled={idleLocked} onClick={() => { setCancelMode("pending_unpaid"); setPage(1); }}>Sin Pago</button>
				<button type="button" className={"btn btn-sm rounded-pill " + (cancelMode === "cancelled" ? "btn-primary" : "btn-outline-secondary")} disabled={idleLocked} onClick={() => { setCancelMode("cancelled"); setPage(1); }}>Anulados</button>
			</div> : null}
		</div>
		<FilterBar options={options} filters={filters} busy={idleLocked} perPage={perPage} show={show}
			onChange={patch} onReset={() => { setFilters(defaultFilters(tabKey)); setPage(1); }} defaults={defaultFilters(tabKey)}
			onPerPage={(size) => { setPerPage(size); setPage(1); }} onExport={exportExcel} exporting={exporting} exportDisabled={total < 1} />
		{tabKey === "ventas" ? salesKpis() : null}
		{tabKey === "inscritos" && totals ? <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", margin: "10px 0" }}>{enrolledKpi()}{kpiCard("Inscritos", String(totals.enrolled))}{kpiCard("Con kit", String(totals.enrolledWithKit), "var(--wa-verde)")}{kpiCard("Sin kit", String(totals.enrolled - totals.enrolledWithKit), totals.enrolled - totals.enrolledWithKit > 0 ? "var(--wa-oro)" : "var(--wa-tinta)")}</div> : null}
		<Pager page={page} lastPage={lastPage} total={total} unit={unit} busy={loading} onPage={setPage} />
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: tabKey === "ventas" ? (rows.length ? kitTable(true) : <Empty />)
				: tabKey === "inscritos" ? (enrollees.length ? enrolleesTable() : <Empty />)
				: tabKey === "abonos" ? (rows.length ? kitTable(false) : <Empty text="Nadie con abono pendiente con estos filtros." />)
				: tabKey === "sin_producto" ? (students.length ? studentsTable() : <Empty />)
				: (rows.length ? cancellationsTable() : <Empty />)}
		</div>
		{lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit={unit} busy={loading} onPage={setPage} /> : null}

		{historyKit ? <KitHistoryModal campusId={campusId} kitId={historyKit.id} section={SECTION} title={"Historial · " + (historyKit.name || historyKit.title)} subtitle={(historyKit.studentName || "") + (historyKit.studentCode ? " · " + historyKit.studentCode : "")} onClose={() => setHistoryKit(null)} /> : null}
		{paymentKit ? <KitPaymentPrompt campusId={campusId} section={SECTION} kitIds={[paymentKit.id]} studentName={paymentKit.studentName || ""} pendingAmount={paymentKit.pendingAmount ? paymentKit.pendingAmount.value : null}
			onDone={(intentId, message) => { setPaymentKit(null); setPaymentNotice(message); load(); }} onCancel={() => setPaymentKit(null)} /> : null}
		{paymentNotice ? <div className="alert alert-success" style={{ position: "fixed", bottom: "72px", right: "16px", zIndex: 2200, cursor: "pointer" }} onClick={() => setPaymentNotice(null)}>{paymentNotice}</div> : null}
		{kitAction ? <ReasonPrompt
			title={kitAction.type === "cancel" ? "Anular kit" : "Reactivar kit"}
			description={kitAction.type === "cancel"
				? <><b>{kitAction.kit.name}</b> de <b>{kitAction.kit.studentName}</b> pasará a Anulado. No se borra; queda en el historial con tu nombre, fecha y motivo.</>
				: <><b>{kitAction.kit.name}</b> de <b>{kitAction.kit.studentName}</b> volverá a Pendiente y podrá cobrarse hoy mismo. Queda en el historial con tu nombre, fecha y motivo.</>}
			confirmLabel={kitAction.type === "cancel" ? "Anular" : "Reactivar"} confirmClass={kitAction.type === "cancel" ? "btn-warning" : "btn-primary"}
			busy={kitActionBusy} error={kitActionError} onConfirm={onKitAction} onCancel={() => { if (!kitActionBusy) setKitAction(null); }} /> : null}
	</div>;
};

export default ControlVentas;
